import { ChecklistItem, useLightMe } from "@myaccount/_client/hooks";
import { useI18n } from "@wojo/localization";
import dayjs from "dayjs";
import {
    CheckListItemImportanceLevel,
    MyAccountBasicMyTripFragment,
    TripChecklistItemStatus,
    TripStatus,
} from "generated/graphql";
import { orderBy } from "lodash";
import {
    getAllChecklistItems,
    ChecklistViewMode,
    SystemChecklistItemType,
} from "./useChecklist";
import { totalChecklistItemCount } from "./useChecklistCounts";

type NextBestActionBase = {
    item: ChecklistItem;
    loggedInTravelerFirstName: string;
    checklistPercentageCompleted: number;
    trip?: MyAccountBasicMyTripFragment;
};

type NextBestAction = (
    | {
          isLoggedInCustomerItem: true;
          isSecondaryTravelerTermsChecklistItem: true;
          primaryTravelerFirstName: string;
          secondaryTravelerFirstName: string;
          secondaryTravelerTripId: string;
      }
    | {
          isLoggedInCustomerItem: true;
          isSecondaryTravelerTermsChecklistItem?: false;
      }
    | {
          isLoggedInCustomerItem: false;
          isSecondaryTravelerTermsChecklistItem?: false;
          otherTravelerFirstName: string;
      }
) &
    NextBestActionBase;

type IncompleteChecklistItem = ChecklistItem &
    (
        | {
              secondaryTravelerFirstName?: string;
              isSecondaryTravelerTermsChecklistItem?: boolean;
              secondaryTravelerTripId?: string;
          }
        | {
              firstName?: string;
          }
    );
const TERMS_SIGNATURE_CHECKLIST_ITEM_ID: SystemChecklistItemType =
    "TermsSignatureChecklistItem";

const checklistImportanceOrder = {
    [CheckListItemImportanceLevel.Required]: 1,
    [CheckListItemImportanceLevel.HighlyRecommended]: 2,
    [CheckListItemImportanceLevel.Recommended]: 3,
};

const getLoggedInTravelerTermsChecklistItem =
    (loggedInTravelerTripId?: string) => (item: IncompleteChecklistItem) =>
        item.id === TERMS_SIGNATURE_CHECKLIST_ITEM_ID &&
        item.tripId === loggedInTravelerTripId;
const getIsSecondaryTravelerTermsChecklistItem = (
    item: IncompleteChecklistItem,
) => item.id === TERMS_SIGNATURE_CHECKLIST_ITEM_ID;
const getChecklistItemImportanceOrder = (item: IncompleteChecklistItem) =>
    checklistImportanceOrder[item.importanceLevel];
const getChecklistItemDueDate = (item: IncompleteChecklistItem) =>
    dayjs(item.dueDate);

/**
 * Gets the soonest trip with an incomplete checklist
 * @param {MyAccountBasicMyTripFragment[]} trips
 * @returns {MyAccountBasicMyTripFragment | undefined}
 */
const getNextTripWithIncompleteChecklist = (
    trips: MyAccountBasicMyTripFragment[],
): MyAccountBasicMyTripFragment | undefined => {
    return trips.find(
        (trip) =>
            trip.status === TripStatus.Confirmed &&
            (trip.booking?.trips ?? []).reduce(
                (acc, bookingTrip) =>
                    totalChecklistItemCount(bookingTrip) + acc,
                0,
            ),
    );
};

/**
 * Gets the next best checklist action for a logged in user or the other travelers on the trip
 * @param {MyAccountBasicMyTripFragment[] } trips
 * @returns {NextBestAction | null}
 */
export const useNextBestAction = (
    trips: MyAccountBasicMyTripFragment[],
): NextBestAction | null => {
    const { customer } = useLightMe();
    const l10n = useI18n("account").account;
    const trip = getNextTripWithIncompleteChecklist(trips);

    if (!customer?._id || !trip?.booking) {
        return null;
    }

    const {
        loggedInTravelerFirstName,
        loggedInTravelerTripId,
        incompleteChecklistItems,
        completeChecklistItems,
        otherTravelersChecklistItems,
    } = trip.booking.trips.reduce(
        (acc, bookingTrip) => {
            const checklistItems = trip.booking
                ? getAllChecklistItems(
                      bookingTrip,
                      trip.booking._id,
                      l10n,
                      ChecklistViewMode.PrimaryViewingOther,
                      bookingTrip.customer?.firstName || "",
                  )
                : [];
            const incompleteItems = checklistItems.filter(
                (item) => item.status !== TripChecklistItemStatus.Complete,
            );
            const completeItems = checklistItems.filter(
                (item) => item.status === TripChecklistItemStatus.Complete,
            );

            if (bookingTrip.customer?._id === customer._id) {
                acc.loggedInTravelerFirstName =
                    bookingTrip.customer.firstName || "";
                acc.loggedInTravelerTripId = bookingTrip._id;
                acc.incompleteChecklistItems.push(...incompleteItems);
                acc.completeChecklistItems.push(...completeItems);
            } else {
                const [termsItems, otherItems] = incompleteItems.reduce<
                    [IncompleteChecklistItem[], IncompleteChecklistItem[]]
                >(
                    (acc, item) => {
                        const secondaryTravelerFirstName =
                            bookingTrip.customer?.firstName || "";
                        if (
                            trip.isPrimary &&
                            getIsSecondaryTravelerTermsChecklistItem(item)
                        ) {
                            acc[0].push({
                                ...item,
                                secondaryTravelerFirstName,
                                isSecondaryTravelerTermsChecklistItem: true,
                                secondaryTravelerTripId: bookingTrip._id,
                            });
                        } else {
                            acc[1].push({
                                ...item,
                                firstName: secondaryTravelerFirstName,
                            });
                        }
                        return acc;
                    },
                    [[], []] as [
                        IncompleteChecklistItem[],
                        IncompleteChecklistItem[],
                    ],
                );
                acc.incompleteChecklistItems.push(...termsItems);
                acc.otherTravelersChecklistItems.push(...otherItems);
            }
            return acc;
        },
        {
            loggedInTravelerFirstName: "",
            loggedInTravelerTripId: "",
            incompleteChecklistItems: [] as Array<
                ChecklistItem & {
                    secondaryTravelerFirstName?: string;
                    isSecondaryTravelerTermsChecklistItem?: boolean;
                    secondaryTravelerTripId?: string;
                }
            >,
            completeChecklistItems: [] as ChecklistItem[],
            otherTravelersChecklistItems: [] as Array<
                ChecklistItem & { firstName?: string }
            >,
        },
    );

    if (
        !incompleteChecklistItems.length &&
        !otherTravelersChecklistItems.length
    ) {
        return null;
    }

    const completedItemsCount = completeChecklistItems.length;
    const allItemsCount = incompleteChecklistItems.length + completedItemsCount;
    const checklistPercentageCompleted =
        allItemsCount && completedItemsCount
            ? Math.round((completedItemsCount / allItemsCount) * 100)
            : 0;

    if (incompleteChecklistItems.length) {
        const sortedPrimaryChecklistItems = orderBy(
            incompleteChecklistItems,
            [
                getLoggedInTravelerTermsChecklistItem(loggedInTravelerTripId),
                getIsSecondaryTravelerTermsChecklistItem,
                getChecklistItemImportanceOrder,
                getChecklistItemDueDate,
            ],
            ["desc", "desc", "asc", "asc"],
        );
        const primaryNextBestActionBase = {
            item: sortedPrimaryChecklistItems[0],
            loggedInTravelerFirstName,
            checklistPercentageCompleted,
        };

        if (
            primaryNextBestActionBase.item.isSecondaryTravelerTermsChecklistItem
        ) {
            return {
                ...primaryNextBestActionBase,
                isLoggedInCustomerItem: true,
                isSecondaryTravelerTermsChecklistItem: true,
                primaryTravelerFirstName: loggedInTravelerFirstName,
                secondaryTravelerTripId:
                    primaryNextBestActionBase.item.secondaryTravelerTripId ||
                    "",
                secondaryTravelerFirstName:
                    primaryNextBestActionBase.item.secondaryTravelerFirstName ||
                    "",
                trip,
            };
        }

        return {
            ...primaryNextBestActionBase,
            isLoggedInCustomerItem: true,
            trip,
        };
    }

    if (otherTravelersChecklistItems.length) {
        const sortedSecondaryChecklistItems = orderBy(
            otherTravelersChecklistItems,
            [getChecklistItemImportanceOrder, getChecklistItemDueDate],
            ["asc", "asc"],
        );
        const nextBestActionForSecondaryTraveler =
            sortedSecondaryChecklistItems[0];
        return {
            loggedInTravelerFirstName,
            isLoggedInCustomerItem: false,
            otherTravelerFirstName:
                nextBestActionForSecondaryTraveler.firstName || "",
            item: nextBestActionForSecondaryTraveler,
            checklistPercentageCompleted,
            trip,
        };
    }
    return null;
};
